import {
  CalendarDays,
  BedDouble,
  Activity,
  Pill,
  AlertCircle,
  ClipboardList,
} from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    admissionDate: string;
    dischargeDate: string;
    wardBed?: string;
    admittingDoctor: string;
    diagnosis: string[];
    procedures?: string[];
    hospitalCourse: string;
    conditionAtDischarge: string;
    medications: {
      name: string;
      dosage: string;
      frequency: string;
      duration: string;
    }[];
    followUpInstructions: string[];
    emergencyInstructions?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

export default function DischargeSummaryTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-gray-700" />
        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
          Discharge Summary
        </span>
      </div>

      {/* Admission & Discharge Info */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[10px]">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Admission Date:</span>
          <span className="text-gray-800">{data.admissionDate}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Discharge Date:</span>
          <span className="text-gray-800">{data.dischargeDate}</span>
        </div>
        {data.wardBed && (
          <div className="flex items-center gap-1.5">
            <BedDouble className="w-3 h-3 text-gray-500" />
            <span className="font-semibold text-gray-600">Ward / Bed:</span>
            <span className="text-gray-800">{data.wardBed}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-gray-600">Admitting Doctor:</span>
          <span className="text-gray-800">{data.admittingDoctor}</span>
        </div>
      </div>

      {/* Diagnosis */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
          Diagnosis
        </h3>
        <ol className="list-decimal list-inside space-y-0.5 pl-1">
          {(data.diagnosis || []).map((dx, i) => (
            <li key={i} className="text-[10px] text-gray-800">
              {dx}
            </li>
          ))}
        </ol>
      </div>

      {/* Procedures */}
      {data.procedures && data.procedures.length > 0 && (
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
            Procedures Performed
          </h3>
          <ol className="list-decimal list-inside space-y-0.5 pl-1">
            {data.procedures.map((proc, i) => (
              <li key={i} className="text-[10px] text-gray-800">
                {proc}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Hospital Course */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Activity className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Hospital Course
          </h3>
        </div>
        <p className="text-[10px] text-gray-700 leading-relaxed pl-1">{data.hospitalCourse}</p>
      </div>

      {/* Condition at Discharge */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
          Condition at Discharge
        </h3>
        <p className="text-[11px] text-gray-800 font-medium pl-1">{data.conditionAtDischarge}</p>
      </div>

      {/* Discharge Medications */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Pill className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Discharge Medications
          </h3>
        </div>
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                #
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Medicine
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Dosage
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Frequency
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Duration
              </th>
            </tr>
          </thead>
          <tbody>
            {(data.medications || []).map((med, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-500 text-center">
                  {i + 1}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 font-medium text-gray-800">
                  {med.name}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{med.dosage}</td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                  {med.frequency}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                  {med.duration}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Follow-Up Instructions */}
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
          Follow-Up Instructions
        </h3>
        <ol className="list-decimal list-inside space-y-0.5 pl-1">
          {(data.followUpInstructions || []).map((instruction, i) => (
            <li key={i} className="text-[10px] text-gray-700 leading-snug">
              {instruction}
            </li>
          ))}
        </ol>
      </div>

      {/* Emergency Instructions */}
      {data.emergencyInstructions && (
        <div className="flex items-start gap-2 bg-red-50 px-3 py-2 border border-red-200 rounded">
          <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-[10px] font-bold uppercase text-red-700 mb-0.5">
              Emergency Contact
            </h3>
            <p className="text-[10px] text-red-700">{data.emergencyInstructions}</p>
          </div>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
